import { Languages, Volume2, Image, Trash2, Loader2, Zap, Check, X } from 'lucide-react';
import { JobProgress } from '../../hooks/useWords';

interface BulkActionsProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onGenerateTranslations: () => void;
  onGenerateAudio: () => void;
  onGenerateImages: () => void;
  onGenerateAll: () => void;
  onDelete: () => void;
  isGenerating?: boolean;
  progress?: JobProgress | null;
}

const PHASE_LABELS: Record<string, string> = {
  translation: 'Перевод',
  audio: 'Аудио',
  image: 'Изображения',
};

const buttonClass =
  'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50 disabled:hover:bg-transparent';

export function BulkActions({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onGenerateTranslations,
  onGenerateAudio,
  onGenerateImages,
  onGenerateAll,
  onDelete,
  isGenerating,
  progress,
}: BulkActionsProps) {
  const hasSelection = selectedCount > 0;
  const allSelected = hasSelection && selectedCount >= totalCount;
  // Generation with nothing selected means "every word in the topic" — say so, instead
  // of leaving the buttons to act on an invisible, surprising scope.
  const scope = hasSelection
    ? `Действует на выбранные ${selectedCount} из ${totalCount} слов`
    : `Без выделения — генерация для всей темы (${totalCount} слов)`;

  return (
    <div className="space-y-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-brand-800">
          Выбрано: {selectedCount} из {totalCount}
        </span>
        {!allSelected && (
          <button
            onClick={onSelectAll}
            disabled={totalCount === 0}
            className={buttonClass}
            title="Выделить все слова темы"
          >
            <Check size={14} />
            Выбрать все
          </button>
        )}
        {hasSelection && (
          <button onClick={onClearSelection} className={buttonClass} title="Снять выделение">
            <X size={14} />
            Снять выделение
          </button>
        )}
        <div className="mx-2 h-4 w-px bg-brand-200" />
        <button onClick={onGenerateTranslations} disabled={isGenerating} className={buttonClass}>
          <Languages size={14} />
          Переводы
        </button>
        <button onClick={onGenerateAudio} disabled={isGenerating} className={buttonClass}>
          <Volume2 size={14} />
          Аудио
        </button>
        <button onClick={onGenerateImages} disabled={isGenerating} className={buttonClass}>
          <Image size={14} />
          Изображения
        </button>
        <button onClick={onGenerateAll} disabled={isGenerating} className={buttonClass}>
          {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          Всё
        </button>
        <div className="mx-2 h-4 w-px bg-brand-200" />
        <button
          onClick={onDelete}
          disabled={isGenerating || !hasSelection}
          title={hasSelection ? 'Удалить выбранные слова' : 'Сначала выделите слова для удаления'}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
        >
          <Trash2 size={14} />
          Удалить
        </button>
      </div>

      <p className="text-xs text-brand-700">{scope}</p>

      {isGenerating && (
        <div className="text-xs text-brand-800">
          <div className="flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" />
            <span>
              {progress?.phase ? `Этап: ${PHASE_LABELS[progress.phase] || progress.phase}` : 'Запуск задачи...'}
              {progress ? ` — ${progress.phaseProgress.completed} из ${progress.phaseProgress.total}` : ''}
              {progress && progress.phaseProgress.failed > 0
                ? `, ошибок: ${progress.phaseProgress.failed}`
                : ''}
            </span>
          </div>
          {progress && progress.phaseProgress.total > 0 && (
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-brand-100">
              <div
                className="h-full rounded-full bg-brand-600 transition-all"
                style={{
                  width: `${Math.min(
                    100,
                    Math.round(
                      ((progress.phaseProgress.completed + progress.phaseProgress.failed) /
                        progress.phaseProgress.total) *
                        100,
                    ),
                  )}%`,
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}